// src/pages/Administration/Members/memberFields.ts
/**
 * What an administrator may change about a member from this screen, and what they may not.
 *
 * This is the safety contract for `/Administration/Members`, kept in one file so it can be read in one
 * sitting rather than inferred from a form. The rule it encodes: **this screen corrects contact and
 * personal details. It does not touch identity, membership standing, or anything another system owns.**
 *
 * ⚠️ `userId` is the account this member is attached to. Overwriting it does not rename a person, it
 * hands their record to somebody else, and nothing on this screen would show that it happened. It is
 * therefore not in `EDITABLE_FIELDS`, is never put in a payload, and is displayed read-only so it can
 * still be copied when someone needs it for support.
 *
 * ⚠️ Writes go out as **`patch`**, carrying only the fields that actually changed. `update` is a PUT:
 * the profile screen sends a whole-object PUT that omits `userId`, which is safe only because this
 * backend merges rather than replaces. That is an assumption, not a guarantee, and it is not one worth
 * making with an admin editing somebody else's record.
 */
import type { Member } from '@actschurch/ccms-sdk';

/** The fields this screen is allowed to write. Anything absent here can never reach a payload. */
export const EDITABLE_FIELDS = [
  'firstName', 'middleName', 'lastName',
  'dateOfBirth', 'gender', 'maritalStatusID',
  'email', 'cellNumber', 'altContact', 'telephone',
] as const;

export type EditableField = typeof EDITABLE_FIELDS[number];

/**
 * Shown, never written. Each is here for a reason, not just caution:
 *
 * - `userId` — the account link. See the file header.
 * - `memberID` — the primary key.
 * - `memberCode` — issued by the church office, not typed in an admin console.
 * - `typeID` — drives who a person is to the rest of Acts Care. Set once at creation; changing it later
 *   is a membership decision, not a correction.
 * - `isMember` / `isActive` — membership standing, and `isActive` is the closest thing this system has
 *   to deleting a person. Deleting was explicitly excluded from this screen, so deactivating is too.
 * - `avatar` — owned by the profile screen, where the person manages their own.
 * - `planningCentre*` — written by the Planning Centre sync. A value typed here would be overwritten by
 *   the next sync anyway, so an editable field would be a lie.
 */
export const PROTECTED_FIELDS = [
  'memberID', 'userId', 'memberCode', 'typeID', 'isMember', 'isActive', 'avatar',
  'planningCentrePersonId', 'planningCentreLinkedAt', 'planningCentreLinkSource',
  'createdDateTime', 'modifiedDateTime',
] as const;

/** The form's working shape. Strings throughout, because that is what inputs give back. */
export interface MemberFormValues {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  maritalStatusID: number | null;
  email: string;
  cellNumber: string;
  altContact: string;
  telephone: string;
}

export const EMPTY_FORM: MemberFormValues = {
  firstName: '', middleName: '', lastName: '', dateOfBirth: '', gender: '',
  maritalStatusID: null, email: '', cellNumber: '', altContact: '', telephone: '',
};

/**
 * Normalises `dateOfBirth` for **writing**.
 *
 * ⚠️ Reads arrive in two shapes — `yyyyMMdd` from some endpoints and an ISO timestamp from others (see
 * `formatBirthDate`). Writes go out as `yyyy-MM-dd`, which both the profile screen and the merge flow
 * already send and the API accepts. Documented rather than left implicit because a silent format change
 * here would be invisible until somebody's birthday moved.
 */
export function toWireDate(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{8}$/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

const text = (v: string) => {
  const t = v.trim();
  return t === '' ? null : t;
};

/** The member as this form sees it, so "changed" compares like with like. */
export function formFromMember(m: Member): MemberFormValues {
  return {
    firstName: m.firstName ?? '',
    middleName: m.middleName ?? '',
    lastName: m.lastName ?? '',
    dateOfBirth: toWireDate(String(m.dateOfBirth ?? '')) ?? '',
    gender: m.gender ?? '',
    maritalStatusID: m.maritalStatusID ?? null,
    email: m.email ?? '',
    cellNumber: m.cellNumber ?? '',
    altContact: m.altContact ?? '',
    telephone: m.telephone ?? '',
  };
}

/**
 * The patch payload: **only** fields that both are editable and actually differ.
 *
 * ⚠️ Sending an unchanged field is not harmless. Two admins on the same record would each overwrite the
 * other's untouched fields with whatever their form loaded, and neither would see a conflict. A payload
 * of only real edits keeps concurrent corrections to different fields from fighting.
 */
export function changedFields(
  original: MemberFormValues,
  next: MemberFormValues,
): Partial<Record<EditableField, string | number | null>> {
  const payload: Partial<Record<EditableField, string | number | null>> = {};

  for (const key of EDITABLE_FIELDS) {
    if (key === 'maritalStatusID') {
      if (next.maritalStatusID !== original.maritalStatusID && next.maritalStatusID != null) {
        payload.maritalStatusID = next.maritalStatusID;
      }
      continue;
    }
    if (key === 'dateOfBirth') {
      if (next.dateOfBirth.trim() !== original.dateOfBirth.trim()) {
        payload.dateOfBirth = toWireDate(next.dateOfBirth);
      }
      continue;
    }
    if (next[key].trim() !== original[key].trim()) payload[key] = text(next[key]);
  }

  return payload;
}

/** Field-level validation. Returns a message per invalid field, empty when the form is good. */
export function validateForm(v: MemberFormValues, { requireEmail = true } = {}) {
  const errors: Partial<Record<keyof MemberFormValues, string>> = {};

  if (!v.firstName.trim()) errors.firstName = 'A first name is required.';
  if (!v.lastName.trim()) errors.lastName = 'A surname is required.';

  const email = v.email.trim();
  // ⚠️ `Member.email` is non-optional on the entity, so an existing record must never be emptied.
  if (requireEmail && !email) errors.email = 'An email address is required.';
  if (email && !/^\S+@\S+\.\S+$/.test(email)) errors.email = 'That does not look like an email address.';

  if (v.dateOfBirth.trim() && !toWireDate(v.dateOfBirth)) {
    errors.dateOfBirth = 'Use a real date.';
  } else if (v.dateOfBirth.trim()) {
    const dob = new Date(toWireDate(v.dateOfBirth) as string);
    if (dob.getTime() > Date.now()) errors.dateOfBirth = 'A date of birth cannot be in the future.';
  }

  return errors;
}
